/**
 * AlertBox Component
 * Reusable alert/notification component with consistent styling
 *
 * Features:
 * - Four variants: success, error, warning, info
 * - Optional title and icon
 * - Dismissible alerts with onDismiss callback
 * - Consistent color coding and styling
 * - Supports both simple messages and React nodes
 */

import type { ReactNode } from 'react';
import {
  SUCCESS_MESSAGE_STYLE,
  ERROR_MESSAGE_STYLE,
  WARNING_MESSAGE_STYLE,
} from '../../styles/modalStyles';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface AlertBoxProps {
  type: AlertType;
  title?: string;
  message: string | ReactNode;
  icon?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const INFO_STYLE = {
  padding: '12px',
  backgroundColor: '#eff6ff',
  border: '1px solid #3b82f6',
  borderRadius: '6px',
  color: '#1e40af',
  fontSize: '14px',
  marginBottom: '16px',
} as const;

const ALERT_STYLES: Record<AlertType, typeof SUCCESS_MESSAGE_STYLE> = {
  success: SUCCESS_MESSAGE_STYLE,
  error: ERROR_MESSAGE_STYLE,
  warning: WARNING_MESSAGE_STYLE,
  info: INFO_STYLE,
};

const DEFAULT_ICONS: Record<AlertType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠️',
  info: 'ℹ',
};

export function AlertBox({
  type,
  title,
  message,
  icon,
  dismissible = false,
  onDismiss,
}: AlertBoxProps) {
  const displayIcon = icon !== undefined ? icon : DEFAULT_ICONS[type];

  return (
    <div style={ALERT_STYLES[type]}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '8px',
      }}>
        <div style={{ flex: 1 }}>
          {title && (
            <div style={{
              fontWeight: '600',
              marginBottom: message ? '8px' : 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              {displayIcon && <span>{displayIcon}</span>}
              <span>{title}</span>
            </div>
          )}
          {message && (
            <div style={{
              fontSize: '13px',
              ...(title ? {} : { display: 'flex', alignItems: 'center', gap: '8px' }),
            }}>
              {!title && displayIcon && <span>{displayIcon}</span>}
              {typeof message === 'string' ? <span>{message}</span> : message}
            </div>
          )}
        </div>
        {dismissible && onDismiss && (
          <button
            onClick={onDismiss}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              fontSize: '18px',
              lineHeight: 1,
              opacity: 0.6,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
            aria-label="Dismiss alert"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}
